// Regular admin inspections and camera checks: when each vehicle is next due, and how that should read.
//
// "Admin inspection" is the existing vehicle_inspections rows with inspection_type = 'admin'. A camera check is a
// row in vehicle_camera_checks. Either one resets that vehicle's clock; the next one is due `interval` months later.
// The interval is a company-wide setting (3 months by default) that a vehicle can override.
import { formatDate, getAppTimeZone } from "@/lib/dates";

export type CheckState = "never" | "overdue" | "due_soon" | "ok";

export interface CheckStatus {
  state: CheckState;
  /** The date of the last check (YYYY-MM-DD), or null if there has never been one. */
  lastIso: string | null;
  /** When the next one is due (YYYY-MM-DD); null when there has never been one. */
  nextDueIso: string | null;
  /** Whole days until due (negative = overdue); null when never checked. */
  daysUntil: number | null;
  intervalMonths: number;
}

export interface CheckSettings {
  adminInspectionMonths: number;
  cameraCheckMonths: number;
  dueSoonDays: number;
}

export const DEFAULT_CHECK_SETTINGS: CheckSettings = {
  adminInspectionMonths: 3,
  cameraCheckMonths: 3,
  dueSoonDays: 14,
};

export const CHECK_SETTING_NAMES = [
  "admin_inspection_interval_months",
  "camera_check_interval_months",
  "fleet_checks_due_soon_days",
] as const;

export const CAMERA_STORAGE_LABELS: Record<string, string> = {
  hard_drive: "Hard drive",
  sd_card: "SD / storage card",
};

const positiveInt = (raw: unknown, fallback: number): number => {
  const n = parseInt(String(raw ?? ""), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/** Reads the three settings out of `settings` rows ({name, value}), falling back to the defaults. */
export function parseCheckSettings(rows: { name: string | null; value: string | null }[] | null | undefined): CheckSettings {
  const by = new Map((rows ?? []).map((r) => [r.name, r.value]));
  return {
    adminInspectionMonths: positiveInt(by.get("admin_inspection_interval_months"), DEFAULT_CHECK_SETTINGS.adminInspectionMonths),
    cameraCheckMonths: positiveInt(by.get("camera_check_interval_months"), DEFAULT_CHECK_SETTINGS.cameraCheckMonths),
    dueSoonDays: positiveInt(by.get("fleet_checks_due_soon_days"), DEFAULT_CHECK_SETTINGS.dueSoonDays),
  };
}

const isoOf = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** `iso` plus whole months; clamps to the end of a shorter month (31 Jan + 1 month = 28/29 Feb). */
export function addMonthsIso(iso: string, months: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const target = new Date(y, m - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  return isoOf(target);
}

/** Whole days from `fromIso` to `toIso` (both YYYY-MM-DD). */
export function daysBetweenIso(fromIso: string, toIso: string): number {
  const [fy, fm, fd] = fromIso.slice(0, 10).split("-").map(Number);
  const [ty, tm, td] = toIso.slice(0, 10).split("-").map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/** A timestamp as a YYYY-MM-DD day in the company's time zone. */
export function localIsoDay(ts: string | Date, timeZone: string = getAppTimeZone()): string {
  return new Date(ts).toLocaleDateString("en-CA", { timeZone });
}

/** Where a vehicle stands on one kind of check. */
export function computeCheckStatus(
  lastIso: string | null | undefined,
  intervalMonths: number,
  todayIso: string,
  dueSoonDays: number
): CheckStatus {
  if (!lastIso) {
    return { state: "never", lastIso: null, nextDueIso: null, daysUntil: null, intervalMonths };
  }
  const last = lastIso.slice(0, 10);
  const nextDueIso = addMonthsIso(last, intervalMonths);
  const daysUntil = daysBetweenIso(todayIso, nextDueIso);
  const state: CheckState = daysUntil < 0 ? "overdue" : daysUntil <= dueSoonDays ? "due_soon" : "ok";
  return { state, lastIso: last, nextDueIso, daysUntil, intervalMonths };
}

/** True when the check needs someone's attention now (never done, overdue, or due within the window). */
export const needsAttention = (s: CheckStatus | null | undefined): boolean =>
  !!s && (s.state === "never" || s.state === "overdue" || s.state === "due_soon");

/** Short text for a status, e.g. "Overdue 12 days", "Due in 5 days", "Never checked". */
export function statusLabel(s: CheckStatus): string {
  const plural = (n: number) => `${n} day${n === 1 ? "" : "s"}`;
  switch (s.state) {
    case "never":
      return "Never checked";
    case "overdue":
      return `Overdue ${plural(Math.abs(s.daysUntil ?? 0))}`;
    case "due_soon":
      return s.daysUntil === 0 ? "Due today" : `Due in ${plural(s.daysUntil ?? 0)}`;
    default:
      return `Due ${formatDate(s.nextDueIso)}`;
  }
}

/** Sort key: never-checked and most-overdue first, then soonest due. */
export const checkUrgency = (s: CheckStatus | null | undefined): number =>
  !s ? Number.MAX_SAFE_INTEGER : s.state === "never" ? -1_000_000 : (s.daysUntil ?? Number.MAX_SAFE_INTEGER);

export interface VehicleForChecks {
  vehicle_number: string;
  registration?: string | null;
  status?: string | null;
  has_camera?: boolean | null;
  camera_type?: string | null;
  camera_storage?: string | null;
  camera_id?: string | null;
  admin_inspection_interval_months?: number | null;
  camera_check_interval_months?: number | null;
}

export interface VehicleCheckRow {
  vehicle_number: string;
  registration: string;
  hasCamera: boolean;
  cameraType: string | null;
  cameraStorage: string | null;
  cameraId: string | null;
  admin: CheckStatus;
  /** % done of an admin inspection that has been started but not finished (1-99); null when none is in progress. */
  adminProgress: number | null;
  /** null when the vehicle has no camera, so no camera check applies. */
  camera: CheckStatus | null;
}

/** Vehicles that are tracked: in service (active, or in the workshop). Inactive and retired ones are skipped. */
export const isTrackedVehicle = (v: Pick<VehicleForChecks, "status">): boolean => {
  const s = (v.status ?? "").trim().toLowerCase();
  return s === "active" || s === "maintenance";
};

/**
 * One row per tracked vehicle.
 * @param lastAdmin  vehicle number -> date (YYYY-MM-DD) of its latest completed admin inspection
 * @param lastCamera vehicle number -> date (YYYY-MM-DD) of its latest camera check
 * @param adminProgress vehicle number -> % done of an admin inspection started but not yet completed
 */
export function buildVehicleChecks(
  vehicles: VehicleForChecks[],
  lastAdmin: Map<string, string>,
  lastCamera: Map<string, string>,
  settings: CheckSettings,
  todayIso: string,
  adminProgress: Map<string, number> = new Map()
): VehicleCheckRow[] {
  return vehicles
    .filter(isTrackedVehicle)
    .map((v) => {
      const hasCamera = v.has_camera !== false;
      return {
        vehicle_number: v.vehicle_number,
        registration: v.registration ?? "",
        hasCamera,
        cameraType: v.camera_type?.trim() || null,
        cameraStorage: v.camera_storage ?? null,
        cameraId: v.camera_id?.trim() || null,
        admin: computeCheckStatus(
          lastAdmin.get(v.vehicle_number),
          v.admin_inspection_interval_months ?? settings.adminInspectionMonths,
          todayIso,
          settings.dueSoonDays
        ),
        adminProgress: adminProgress.get(v.vehicle_number) ?? null,
        camera: hasCamera
          ? computeCheckStatus(
              lastCamera.get(v.vehicle_number),
              v.camera_check_interval_months ?? settings.cameraCheckMonths,
              todayIso,
              settings.dueSoonDays
            )
          : null,
      };
    });
}

export interface CheckCounts {
  adminOverdue: number;
  adminDueSoon: number;
  cameraOverdue: number;
  cameraDueSoon: number;
  /** Number of checks (admin + camera) needing attention. */
  total: number;
}

/** Overdue includes "never checked". */
export function summarizeChecks(rows: VehicleCheckRow[]): CheckCounts {
  const c: CheckCounts = { adminOverdue: 0, adminDueSoon: 0, cameraOverdue: 0, cameraDueSoon: 0, total: 0 };
  for (const r of rows) {
    if (r.admin.state === "never" || r.admin.state === "overdue") c.adminOverdue++;
    else if (r.admin.state === "due_soon") c.adminDueSoon++;
    if (r.camera) {
      if (r.camera.state === "never" || r.camera.state === "overdue") c.cameraOverdue++;
      else if (r.camera.state === "due_soon") c.cameraDueSoon++;
    }
  }
  c.total = c.adminOverdue + c.adminDueSoon + c.cameraOverdue + c.cameraDueSoon;
  return c;
}

/** Tailwind text colour for a status, matching the rest of the fleet screens. */
export const checkTextClass = (s: CheckStatus | null | undefined): string => {
  switch (s?.state) {
    case "never":
    case "overdue":
      return "text-red-400";
    case "due_soon":
      return "text-amber-400";
    case "ok":
      return "text-green-400";
    default:
      return "text-muted-foreground";
  }
};
